// Shared 401 token-refresh-and-retry interceptor. Extracted so it can be
// attached to every axios instance the app uses — previously it only lived on
// axios's bare default instance (via AuthContext.js), so the 29 files that
// import utils/axios.js's separate axios.create() instance never got
// auto-refresh on a 401 (axios interceptors don't inherit across instances).
import axios from 'axios';
import { BACKEND_URL } from './config';
import { safeLocalStorage, safeSessionStorage } from './safeStorage';
import { authError } from '@/utils/authError';

const API = `${BACKEND_URL}/api`;

// Module-level singleton state, intentionally shared across every instance
// this is attached to — a 401 on one instance must not race a concurrent
// refresh triggered by a 401 on another instance.
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Endpoints that must never trigger a refresh, because a refresh IS one of
// them or a 401 from them is the answer rather than a problem.
//
// `/auth/me` used to be in this list "to prevent redirect loops", and that was
// the whole web logout bug: the access cookie lives 24h, so the first
// `/auth/me` after a day 401s, no refresh was ever attempted, and AuthContext
// cleared the session. Every returning member was signed out once a day, with
// a perfectly good refresh token sitting in storage.
//
// There is no loop to prevent. `/auth/refresh-token` stays excluded, so a
// failing refresh cannot recurse, and `originalRequest._retry` is set before
// the retry, so a second 401 falls straight through.
const AUTH_PATHS = ['/auth/login', '/auth/register', '/auth/refresh-token', '/auth/logout'];

/** Set on a 401 the interceptor deliberately let through (see below). */
export const TRANSIENT_REFRESH_FAILURE = 'wfTransientRefreshFailure';

/**
 * Attach the 401 refresh-and-retry interceptor to the given axios instance.
 */
export function attachAuthRefreshInterceptor(instance) {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Only handle 401s (not 403s, not retries)
      if (error.response?.status !== 401 || originalRequest._retry) {
        return Promise.reject(error);
      }

      // Skip refresh for auth endpoints themselves (including /auth/me to prevent redirect loops)
      if (AUTH_PATHS.some(p => originalRequest.url?.includes(p))) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue concurrent 401s — single refresh at a time
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(newToken => {
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return instance(originalRequest);
        }).catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = safeLocalStorage.getItem('wefaqna_refresh_token');
      if (!refreshToken) {
        isRefreshing = false;
        safeLocalStorage.removeItem('wefaqna_refresh_token');
        safeLocalStorage.removeItem('user');

        // `/auth/me` is the session CHECK, and it now reaches this
        // interceptor at all — it used to be excluded. A hard redirect here
        // would yank somebody off a public page (an article, the FAQ) the
        // moment a stale cached user failed to verify. Reject quietly and let
        // AuthContext drop them to guest, which is what happened before.
        //
        // Every other request is an action the member deliberately took while
        // believing they were signed in, so saying so beats failing silently.
        if (originalRequest.url?.includes('/auth/me')) {
          return Promise.reject(error);
        }

        safeSessionStorage.setItem(
          'auth_error',
          authError('sessionExpired')
        );
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${API}/auth/refresh-token`, {
          refresh_token: refreshToken
        });

        const newAccessToken = data.access_token;
        const newRefreshToken = data.refresh_token;

        // access_token is set as httpOnly cookie by backend — no localStorage needed
        if (newRefreshToken) {
          safeLocalStorage.setItem('wefaqna_refresh_token', newRefreshToken);
        }

        processQueue(null, newAccessToken);

        // Cookie will be sent automatically via withCredentials
        return instance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Only log out on confirmed auth failure (401/403). Network errors,
        // timeouts, and 5xx (including the refresh endpoint's 503 catch-all)
        // are transient — keep the session and let the user retry.
        const refreshStatus = refreshError?.response?.status;
        const isAuthFailure = refreshStatus === 401 || refreshStatus === 403;
        if (!isAuthFailure) {
          // Mark it, so the caller can tell this 401 apart from a real one.
          // Without the marker AuthContext clears the session on the bare
          // status code, undoing the decision made right here.
          error[TRANSIENT_REFRESH_FAILURE] = true;
          if (refreshError) refreshError[TRANSIENT_REFRESH_FAILURE] = true;
        }
        if (isAuthFailure) {
          safeLocalStorage.removeItem('wefaqna_refresh_token');
          safeLocalStorage.removeItem('user');
          safeSessionStorage.setItem(
            'auth_error',
            authError('sessionExpired')
          );
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
}
